import { z } from "zod";
/** Default plan for existing orgs — all limits null (unlimited), all features on. */
export const UNRESTRICTED_PLAN_ID = "plan_unrestricted";
export const UNRESTRICTED_PLAN_CODE = "unrestricted";
export const UNRESTRICTED_PLAN_NAME = "Unrestricted (legacy default)";
export const ENTITLEMENT_LIMIT_EXCEEDED = "ENTITLEMENT_LIMIT_EXCEEDED";
export const ENTITLEMENT_FEATURE_DENIED = "ENTITLEMENT_FEATURE_DENIED";
export const ENTITLEMENT_LIMIT_KEYS = [
    "maxActiveSites",
    "maxActiveMembers",
    "maxStorageBytes",
];
export const ENTITLEMENT_FEATURES = ["publish", "bundleGenerate", "sponsored"];
/** null = unlimited */
export const nullableLimitSchema = z.number().int().nonnegative().nullable();
export const warnPercentSchema = z.number().int().min(1).max(100);
export const commercialPlanSchema = z.object({
    id: z.string(),
    code: z.string(),
    displayName: z.string(),
    description: z.string().nullable(),
    maxActiveSites: nullableLimitSchema,
    maxActiveMembers: nullableLimitSchema,
    maxStorageBytes: nullableLimitSchema,
    allowPublish: z.boolean(),
    allowBundleGenerate: z.boolean(),
    allowSponsored: z.boolean(),
    warnSitesPercent: warnPercentSchema,
    warnMembersPercent: warnPercentSchema,
    warnStoragePercent: warnPercentSchema,
    active: z.boolean(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const createCommercialPlanSchema = z.object({
    code: z
        .string()
        .trim()
        .min(2)
        .max(64)
        .regex(/^[a-z0-9][a-z0-9_-]*$/i, "Invalid plan code"),
    displayName: z.string().trim().min(1).max(120),
    description: z.string().trim().max(500).nullable().optional(),
    maxActiveSites: nullableLimitSchema.optional(),
    maxActiveMembers: nullableLimitSchema.optional(),
    maxStorageBytes: nullableLimitSchema.optional(),
    allowPublish: z.boolean().optional(),
    allowBundleGenerate: z.boolean().optional(),
    allowSponsored: z.boolean().optional(),
    warnSitesPercent: warnPercentSchema.optional(),
    warnMembersPercent: warnPercentSchema.optional(),
    warnStoragePercent: warnPercentSchema.optional(),
    active: z.boolean().optional(),
});
export const updateCommercialPlanSchema = createCommercialPlanSchema
    .omit({ code: true })
    .partial()
    .extend({
    displayName: z.string().trim().min(1).max(120).optional(),
});
export const assignOrganizationEntitlementSchema = z.object({
    planId: z.string().min(1),
    /** null clears override (use plan); omit leaves unchanged on PATCH-style — full replace on PUT */
    overrideMaxActiveSites: nullableLimitSchema.optional(),
    overrideMaxActiveMembers: nullableLimitSchema.optional(),
    overrideMaxStorageBytes: nullableLimitSchema.optional(),
    overrideAllowPublish: z.boolean().nullable().optional(),
    overrideAllowBundleGenerate: z.boolean().nullable().optional(),
    overrideAllowSponsored: z.boolean().nullable().optional(),
    notes: z.string().trim().max(500).nullable().optional(),
});
export const usageMetricSchema = z.object({
    usage: z.number().int().nonnegative(),
    max: nullableLimitSchema,
    warnPercent: warnPercentSchema,
    warning: z.boolean(),
    exceeded: z.boolean(),
});
export const organizationEntitlementStatusSchema = z.object({
    organizationId: z.string(),
    planId: z.string(),
    planCode: z.string(),
    planDisplayName: z.string(),
    notes: z.string().nullable(),
    limits: z.object({
        maxActiveSites: nullableLimitSchema,
        maxActiveMembers: nullableLimitSchema,
        maxStorageBytes: nullableLimitSchema,
    }),
    features: z.object({
        publish: z.boolean(),
        bundleGenerate: z.boolean(),
        sponsored: z.boolean(),
    }),
    usage: z.object({
        activeSites: usageMetricSchema,
        activeMembers: usageMetricSchema,
        storageBytes: usageMetricSchema,
    }),
    overrides: z.object({
        maxActiveSites: nullableLimitSchema.optional(),
        maxActiveMembers: nullableLimitSchema.optional(),
        maxStorageBytes: nullableLimitSchema.optional(),
        allowPublish: z.boolean().nullable().optional(),
        allowBundleGenerate: z.boolean().nullable().optional(),
        allowSponsored: z.boolean().nullable().optional(),
    }),
    assignedAt: z.string(),
    updatedAt: z.string(),
});
//# sourceMappingURL=entitlements.js.map